/**
 * authService.js — Authentication API calls for TapstarQR
 * =========================================================
 * Wraps the /api/v1/auth/* Django REST endpoints.
 */

import httpClient from './httpClient';
import { gaIdsPayload } from '@/lib/ga';

export const authService = {
  /** Login with email + password. Returns { user, message }. */
  login: async (email, password, rememberMe = false) => {
    const { data } = await httpClient.post('/auth/login/', { email, password, remember_me: rememberMe });
    return data;
  },

  /** Register a new account. Returns { user, message }. `ctaId` (optional) is
   * the marketing CTA link id this signup is attributed to — see
   * `src/lib/ctaTracking.js`. The GA4 ids are read here, not passed in, so no
   * caller can forget them — see `src/lib/ga.js`. */
  register: async ({ email, password, username, phone, phonePrefix, preferredLanguage, ctaId }) => {
    const { data } = await httpClient.post('/auth/register/', {
      email, password, username, phone, phone_prefix: phonePrefix, preferredLanguage,
      cta_id: ctaId,
      ...gaIdsPayload(),
    });
    return data;
  },

  /** Logout (clears server session). */
  logout: async () => {
    await httpClient.post('/auth/logout/');
  },

  /** Get the authenticated user's profile. */
  me: async () => {
    const { data } = await httpClient.get('/auth/me/');
    return data;
  },

  /**
   * Identidad cifrada para el chat de soporte (`window.richpanelSettings`).
   * `{ settings: string | null }`; `null` sin secret configurado o impersonando.
   */
  supportIdentity: async () => {
    const { data } = await httpClient.get('/auth/support-identity/');
    return data;
  },

  /** Update editable profile fields (username, phone, preferredLanguage). */
  updateMe: async (fields) => {
    const { data } = await httpClient.patch('/auth/me/', fields);
    return data;
  },

  /** Request a password reset email. */
  requestPasswordReset: async (email) => {
    const { data } = await httpClient.post('/auth/password/reset/', { email });
    return data;
  },

  /** Confirm password reset with token. */
  confirmPasswordReset: async (uid, token, newPassword) => {
    const { data } = await httpClient.post('/auth/password/reset/confirm/', {
      uid,
      token,
      newPassword,
    });
    return data;
  },

  /** Change password for authenticated user. */
  changePassword: async (old_password, new_password, confirm_password) => {
    const { data } = await httpClient.post('/auth/password/change/', {
      old_password, new_password, confirm_password,
    });
    return data;
  },

  // ---------------------------------------------------------
  // Two-Factor Authentication (2FA) API
  // ---------------------------------------------------------

  /** Verify 2FA token during login */
  verify2FA: async (token) => {
    const { data } = await httpClient.post('/auth/2fa/verify/', { token });
    return data;
  },

  /** Check if 2FA is enabled for current user */
  get2FAStatus: async () => {
    const { data } = await httpClient.get('/auth/2fa/status/');
    return data;
  },

  /** Start 2FA setup process */
  setup2FA: async () => {
    const { data } = await httpClient.post('/auth/2fa/setup/');
    return data;
  },

  /** Confirm 2FA setup with a token */
  confirm2FASetup: async (deviceId, token) => {
    const { data } = await httpClient.post('/auth/2fa/setup/confirm/', { 
      device_id: deviceId, 
      token 
    });
    return data;
  },

  /** Disable 2FA */
  disable2FA: async (token) => {
    const { data } = await httpClient.post('/auth/2fa/disable/', { token });
    return data;
  },
};
